import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaPen } from 'react-icons/fa';
import { toast } from 'react-toastify';
import WorkAreaService, { WorkArea, WorkAreaUpdatePayload, Location as WALocation } from './features/workareas/services/WorkAreaService';
import { fetchPositionsForWA, Position, deletePosition } from './features/contracts/services/ContractWizardService';
import EmployeeService from './features/employees/services/EmployeeService';
import { Employee } from './hooks/useEmployeeData';
import PositionEditorDrawer from './features/workareas/components/PositionEditorDrawer';
import { personalakteStyles as ps, colors, WA_PRESET_COLORS } from './features/employees/styles/personalakteStyles';

type TabName = 'overview' | 'positions' | 'people' | 'settings';

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: colors.textMuted,
  marginBottom: 6,
  display: 'block',
};

const valueStyle: React.CSSProperties = {
  fontSize: 14,
  color: colors.navy,
  fontWeight: 500,
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: 14,
  color: colors.navy,
  backgroundColor: colors.bgCard,
};

function WorkAreaDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();

  const [workArea, setWorkArea] = useState<WorkArea | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabName>('overview');
  const [savingManager, setSavingManager] = useState(false);
  const [showPositionEditor, setShowPositionEditor] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [savingColor, setSavingColor] = useState(false);
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const colorDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [checkedMap, setCheckedMap] = useState<Record<number, boolean>>({});
  const [showDeactivatedInModal, setShowDeactivatedInModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editLocation, setEditLocation] = useState<number | null>(null);
  const [locations, setLocations] = useState<WALocation[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    Promise.all([
      WorkAreaService.fetchWorkArea(Number(id)),
      fetchPositionsForWA(Number(id)),
      EmployeeService.fetchAllEmployees(),
    ])
      .then(([waData, positionsData, employeesData]) => {
        setWorkArea(waData);
        setPositions(positionsData);
        setEmployees(employeesData);
      })
      .catch((err) => console.error('Error loading Work Area detail:', err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    return () => {
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
    };
  }, []);

  const handleAssignEmployees = () => {
    if (!workArea) return;
    const initial: Record<number, boolean> = {};
    employees.forEach((emp) => {
      initial[emp.id] = Array.isArray(emp.work_areas) && emp.work_areas.includes(workArea.id);
    });
    setCheckedMap(initial);
    setShowEmployeeModal(true);
  };

  const handleSaveEmployeeAssignments = async () => {
    if (!workArea) return;
    const assignPromises: Promise<void>[] = [];
    employees.forEach((emp) => {
      const shouldHave = !!checkedMap[emp.id];
      const hasNow = Array.isArray(emp.work_areas) && emp.work_areas.includes(workArea.id);
      if (shouldHave && !hasNow) {
        assignPromises.push(WorkAreaService.assignEmployee(workArea.id, { employee_id: emp.id }));
      } else if (!shouldHave && hasNow) {
        assignPromises.push(WorkAreaService.unassignEmployee(workArea.id, { employee_id: emp.id }));
      }
    });

    if (assignPromises.length === 0) {
      setShowEmployeeModal(false);
      return;
    }

    const results = await Promise.allSettled(assignPromises);
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      console.error('Some employee assignments failed:', failures);
      toast.warn(`${ok} of ${assignPromises.length} updates saved`);
    } else {
      toast.success('Employee assignments updated');
    }
    setShowEmployeeModal(false);
    const updatedEmployees = await EmployeeService.fetchAllEmployees();
    setEmployees(updatedEmployees);
  };

  const saveColor = useCallback(async (newColor: string) => {
    if (!workArea || !id) return;

    setSavingColor(true);
    try {
      const updatedWA = await WorkAreaService.updateWorkArea(Number(id), { color: newColor });
      setWorkArea(updatedWA);
    } catch (err: any) {
      console.error('Error updating color:', err);
      toast.error('Fehler beim Speichern der Farbe');
    } finally {
      setSavingColor(false);
      setPendingColor(null);
    }
  }, [workArea, id]);

  const handleColorChange = (newColor: string) => {
    setPendingColor(newColor);

    if (colorDebounceRef.current) {
      clearTimeout(colorDebounceRef.current);
    }

    colorDebounceRef.current = setTimeout(() => {
      saveColor(newColor);
    }, 500);
  };

  const handleStartEdit = async () => {
    if (!workArea) return;
    setEditName(workArea.name);
    setEditAddress(workArea.address || '');
    setEditLocation(workArea.location ?? null);
    setIsEditing(true);
    if (locations.length === 0) {
      try {
        const locs = await WorkAreaService.fetchAllLocations();
        setLocations(locs);
      } catch (err) {
        console.error('Error loading locations:', err);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!workArea || !id || !editName.trim()) return;

    setSavingEdit(true);
    try {
      const updatedWA = await WorkAreaService.updateWorkArea(Number(id), {
        name: editName.trim(),
        address: editAddress.trim() || undefined,
        location: editLocation,
      });
      setWorkArea(updatedWA);
      setIsEditing(false);
      toast.success('Work area updated');
    } catch (err: any) {
      console.error('Error updating work area:', err);
      const errorMsg = err?.response?.data?.detail || err?.data?.detail || 'Failed to update';
      toast.error(errorMsg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleManagerChange = async (field: 'manager' | 'deputy_manager', value: string) => {
    if (!workArea || !id) return;

    const employeeId = value === '' ? null : Number(value);

    setSavingManager(true);
    try {
      const payload: WorkAreaUpdatePayload = {
        [field]: employeeId,
      };

      const updatedWA = await WorkAreaService.updateWorkArea(Number(id), payload);
      setWorkArea(updatedWA);
      toast.success(
        field === 'manager' ? 'Manager updated successfully' : 'Deputy Manager updated successfully'
      );
    } catch (err: any) {
      console.error(`Error updating ${field}:`, err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Failed to update';
      toast.error(errorMsg);
    } finally {
      setSavingManager(false);
    }
  };

  const reloadPositions = async () => {
    if (!id) return;
    try {
      const positionsData = await fetchPositionsForWA(Number(id));
      setPositions(positionsData);
    } catch (err) {
      console.error('Error reloading positions:', err);
    }
  };

  const handleAddPosition = () => {
    setEditingPosition(null);
    setShowPositionEditor(true);
  };

  const handleEditPosition = (position: Position) => {
    setEditingPosition(position);
    setShowPositionEditor(true);
  };

  const handleDeletePosition = async (position: Position) => {
    if (!window.confirm(`Delete position "${position.label}"?`)) return;

    try {
      await deletePosition(position.id);
      toast.success('Position deleted successfully');
      await reloadPositions();
    } catch (err: any) {
      console.error('Error deleting position:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.detail || 'Failed to delete position';
      toast.error(errorMsg);
    }
  };

  const handlePositionSaved = async () => {
    await reloadPositions();
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
        Loading Work Area...
      </div>
    );
  }

  if (!workArea) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
        <Link to="/organization?tab=work-areas" className="hover-back" style={ps.backButton}>
          <FaArrowLeft size={14} />
          Back to Work Areas
        </Link>
        <p style={{ color: colors.textMuted, fontSize: 14 }}>Work Area not found</p>
      </div>
    );
  }

  const tabs: { key: TabName; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'positions', label: 'Positions' },
    { key: 'people', label: 'People' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      <Link to="/organization?tab=work-areas" className="hover-back" style={ps.backButton}>
        <FaArrowLeft size={14} />
        Back to Work Areas
      </Link>

      {/* Header Card */}
      <div style={{ ...ps.card, marginBottom: 20, padding: '24px 28px' }}>
        {isEditing ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{ ...selectStyle, fontWeight: 600 }}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <select
                  value={editLocation != null ? String(editLocation) : ''}
                  onChange={(e) => setEditLocation(e.target.value ? Number(e.target.value) : null)}
                  style={selectStyle}
                >
                  <option value="">-- No Location --</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Address</label>
                <input
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  placeholder="Address..."
                  style={selectStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={handleCancelEdit} className="hover-btn-secondary" style={ps.buttonSecondary} disabled={savingEdit}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="hover-btn-primary" style={ps.buttonPrimary} disabled={savingEdit || !editName.trim()}>
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: colors.navy }}>
                {workArea.name}
              </h1>
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: colors.textLight }}>
                Work Area Details
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleStartEdit} className="hover-btn-secondary" style={ps.buttonSecondary} title="Edit Name & Address">
                <FaPen size={11} style={{ marginRight: 6 }} />
                Edit
              </button>
              <button onClick={handleAssignEmployees} className="hover-btn-primary" style={ps.buttonPrimary}>
                Assign Employees
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs + Content Card */}
      <div style={{ ...ps.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, overflowX: 'auto' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...ps.tabButton(activeTab === tab.key),
                padding: '14px 20px',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24, minHeight: 400 }}>
          {activeTab === 'overview' && (
            <div>
              {/* Basic Information */}
              <div style={{ ...ps.cardCompact, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 600, color: colors.navy }}>
                  Basic Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Name</label>
                    <div style={valueStyle}>{workArea.name}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Location</label>
                    <div style={valueStyle}>{workArea.location_name || 'Keine Location'}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Address</label>
                    <div style={valueStyle}>{workArea.effective_address || workArea.address || 'N/A'}</div>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    {WA_PRESET_COLORS.map((c) => {
                      const selected = (pendingColor || workArea.color || '#5271A5').toLowerCase() === c.toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          disabled={savingColor}
                          onClick={() => handleColorChange(c)}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: c,
                            border: selected ? '2px solid #fff' : '2px solid transparent',
                            boxShadow: selected ? `0 0 0 2px ${c}, 0 1px 4px rgba(0,0,0,0.18)` : 'none',
                            cursor: savingColor ? 'not-allowed' : 'pointer',
                            opacity: savingColor ? 0.6 : 1,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={c}
                        >
                          {selected && (
                            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                              <path d="M3 7l3 3 5-5.5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                    {savingColor && <span style={{ fontSize: 12, color: colors.textMuted }}>Speichert...</span>}
                  </div>
                </div>
              </div>

              {/* Work Area Management */}
              <div style={{ ...ps.cardCompact, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 600, color: colors.navy }}>
                  Work Area Management
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Main Manager</label>
                    <select
                      value={workArea.manager != null ? String(workArea.manager) : ''}
                      onChange={(e) => handleManagerChange('manager', e.target.value)}
                      disabled={savingManager}
                      style={{ ...selectStyle, cursor: savingManager ? 'not-allowed' : 'pointer' }}
                    >
                      <option value="">-- Select Manager --</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={String(emp.id)}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Deputy Manager</label>
                    <select
                      value={workArea.deputy_manager != null ? String(workArea.deputy_manager) : ''}
                      onChange={(e) => handleManagerChange('deputy_manager', e.target.value)}
                      disabled={savingManager}
                      style={{ ...selectStyle, cursor: savingManager ? 'not-allowed' : 'pointer' }}
                    >
                      <option value="">-- Select Deputy --</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={String(emp.id)}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Positions Summary */}
              <div style={{ ...ps.cardCompact, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.navy }}>
                    Positions ({positions.length})
                  </h3>
                  <button
                    onClick={() => setActiveTab('positions')}
                    className="hover-btn-secondary"
                    style={ps.buttonSecondary}
                  >
                    Zu Positions-Tab →
                  </button>
                </div>
                {positions.length === 0 ? (
                  <p style={{ color: colors.textMuted, fontSize: 14, margin: 0 }}>No positions configured yet</p>
                ) : (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {positions.map((pos) => (
                      <span
                        key={pos.id}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: pos.active ? '#e8f5e9' : colors.bgPage,
                          border: `1px solid ${pos.active ? '#81c784' : colors.border}`,
                          borderRadius: 9999,
                          fontSize: 12,
                          color: pos.active ? '#2e7d32' : colors.textMuted,
                          fontWeight: 500,
                        }}
                      >
                        {pos.label}
                        {!pos.active && ' (inactive)'}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Geofence */}
              <div style={{ ...ps.cardCompact, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 600, color: colors.navy }}>
                  Geofence {workArea.location_name && `(von Location: ${workArea.location_name})`}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Latitude</label>
                    <div style={valueStyle}>{workArea.effective_latitude || workArea.latitude || 'N/A'}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Longitude</label>
                    <div style={valueStyle}>{workArea.effective_longitude || workArea.longitude || 'N/A'}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Radius (meters)</label>
                    <div style={valueStyle}>{workArea.effective_radius || workArea.radius_meters || 0}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Address</label>
                    <div style={valueStyle}>{workArea.effective_address || workArea.address || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button
                  onClick={() => setActiveTab('people')}
                  className="hover-btn-secondary"
                  style={ps.buttonSecondary}
                >
                  Zu People-Tab →
                </button>
              </div>
            </div>
          )}

          {activeTab === 'positions' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, color: colors.navy }}>Positions</h2>
                <button onClick={handleAddPosition} className="hover-btn-primary" style={ps.buttonPrimary}>
                  + Add Position
                </button>
              </div>
              <div style={ps.cardCompact}>
                {positions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: colors.textMuted, marginBottom: 16 }}>No positions configured yet</p>
                    <button onClick={handleAddPosition} className="hover-btn-primary" style={ps.buttonPrimary}>
                      + Add First Position
                    </button>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Label</th>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Position Key</th>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Employment Types</th>
                        <th style={{ padding: 12, textAlign: 'left', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Benefits</th>
                        <th style={{ padding: 12, textAlign: 'center', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Active</th>
                        <th style={{ padding: 12, textAlign: 'center', fontSize: 11, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((pos) => (
                        <tr key={pos.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                          <td style={{ padding: 12, fontSize: 13, color: colors.navy, fontWeight: 500 }}>{pos.label}</td>
                          <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12, color: colors.textBody }}>
                            {pos.position_key}
                          </td>
                          <td style={{ padding: 12, fontSize: 12, color: colors.textBody }}>
                            {pos.allowed_employment_types?.join(', ') || 'All'}
                          </td>
                          <td style={{ padding: 12 }}>
                            {pos.default_benefits && Object.entries(pos.default_benefits).filter(([_, v]) => v).length > 0 ? (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {Object.entries(pos.default_benefits)
                                  .filter(([_, enabled]) => enabled)
                                  .map(([key]) => {
                                    const labels: Record<string, string> = {
                                      uniform: 'Uniform',
                                      meal_allowance: 'Essenszuschuss',
                                      service_vehicle: 'Dienstwagen',
                                    };
                                    return (
                                      <span
                                        key={key}
                                        style={{
                                          padding: '2px 8px',
                                          backgroundColor: '#e3f2fd',
                                          border: '1px solid #90caf9',
                                          borderRadius: 9999,
                                          fontSize: 11,
                                          color: '#1976d2',
                                        }}
                                      >
                                        {labels[key] || key}
                                      </span>
                                    );
                                  })}
                              </div>
                            ) : (
                              <span style={{ color: colors.textLight, fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: pos.active ? colors.success : colors.danger,
                              }}
                            />
                          </td>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                              <button
                                onClick={() => handleEditPosition(pos)}
                                className="hover-btn-secondary"
                                style={{ ...ps.buttonSecondary, padding: '4px 12px', fontSize: 12 }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeletePosition(pos)}
                                style={{
                                  ...ps.buttonSecondary,
                                  padding: '4px 12px',
                                  fontSize: 12,
                                  color: colors.danger,
                                  borderColor: colors.danger,
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {activeTab === 'people' && (
            <div>
              <h2 style={{ marginTop: 0, color: colors.navy }}>People</h2>
              <div style={ps.cardCompact}>
                <p style={{ color: colors.textMuted, textAlign: 'center', padding: 40 }}>
                  Employee management coming soon
                </p>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h2 style={{ marginTop: 0, color: colors.navy }}>Settings</h2>
              <div style={ps.cardCompact}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Latitude</label>
                    <div style={valueStyle}>{workArea.latitude || 'N/A'}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Longitude</label>
                    <div style={valueStyle}>{workArea.longitude || 'N/A'}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Radius (meters)</label>
                    <div style={valueStyle}>{workArea.radius_meters || 0}</div>
                  </div>
                  <div>
                    <label style={labelStyle}>Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        background: workArea.color || '#5271A5',
                        border: '1px solid rgba(0,0,0,0.1)',
                      }} />
                      <span style={valueStyle}>{workArea.color || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Employee Assignment Modal */}
      {showEmployeeModal && (
        <div
          onClick={() => setShowEmployeeModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            ...ps.card,
            padding: '28px 32px 22px 32px',
            minWidth: 400,
            maxWidth: 440,
            maxHeight: '88vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(30,40,90,0.18)',
            position: 'relative'
          }}>
            <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 600, color: colors.navy, fontSize: '1.25rem' }}>
              Assign Employees to <span style={{ color: colors.navy }}>{workArea?.name}</span>
            </h2>
            <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
              <button
                onClick={() => {
                  const all: Record<number, boolean> = {};
                  employees.forEach((emp) => { all[emp.id] = true; });
                  setCheckedMap(all);
                }}
                className="hover-btn-secondary"
                style={{ ...ps.buttonSecondary, padding: '7px 16px' }}
              >
                Select all
              </button>
              <button
                onClick={() => {
                  const none: Record<number, boolean> = {};
                  employees.forEach((emp) => { none[emp.id] = false; });
                  setCheckedMap(none);
                }}
                className="hover-btn-secondary"
                style={{ ...ps.buttonSecondary, padding: '7px 16px' }}
              >
                Deselect all
              </button>
            </div>
            <div style={{ marginBottom: 18 }}>
              {employees
                .filter(emp => emp.onboarding_status !== 'DEACTIVATED')
                .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                .map((emp) => (
                <div key={emp.id} style={{ marginBottom: 7, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!checkedMap[emp.id]}
                    onChange={() => setCheckedMap((prev) => ({ ...prev, [emp.id]: !prev[emp.id] }))}
                    style={{ width: 18, height: 18, accentColor: colors.navy, cursor: 'pointer', marginRight: 2 }}
                  />
                  <span style={{ fontSize: 13, color: colors.textBody }}>{emp.first_name} {emp.last_name}</span>
                </div>
              ))}

              {employees.filter(emp => emp.onboarding_status === 'DEACTIVATED').length > 0 && (
                <>
                  <div
                    onClick={() => setShowDeactivatedInModal(!showDeactivatedInModal)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 0', marginTop: 8, cursor: 'pointer',
                      borderTop: `1px solid ${colors.border}`,
                    }}
                  >
                    <span style={{
                      fontSize: 10, color: colors.textLight,
                      transition: 'transform 0.2s',
                      transform: showDeactivatedInModal ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}>▶</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.danger }}>
                      Deactivated ({employees.filter(emp => emp.onboarding_status === 'DEACTIVATED').length})
                    </span>
                  </div>
                  {showDeactivatedInModal && employees
                    .filter(emp => emp.onboarding_status === 'DEACTIVATED')
                    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                    .map((emp) => (
                    <div key={emp.id} style={{ marginBottom: 7, display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6 }}>
                      <input
                        type="checkbox"
                        checked={!!checkedMap[emp.id]}
                        onChange={() => setCheckedMap((prev) => ({ ...prev, [emp.id]: !prev[emp.id] }))}
                        style={{ width: 18, height: 18, accentColor: colors.navy, cursor: 'pointer', marginRight: 2 }}
                      />
                      <span style={{ fontSize: 13, color: colors.textBody }}>{emp.first_name} {emp.last_name}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="hover-btn-secondary"
                style={ps.buttonSecondary}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmployeeAssignments}
                className="hover-btn-primary"
                style={ps.buttonPrimary}
              >
                Save
              </button>
            </div>
            <button
              onClick={() => setShowEmployeeModal(false)}
              style={{
                position: 'absolute', top: 12, right: 16, background: 'none', border: 'none',
                fontSize: 22, color: colors.textLight, cursor: 'pointer', padding: 0, lineHeight: 1
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Position Editor Drawer */}
      {showPositionEditor && id && (
        <PositionEditorDrawer
          workAreaId={Number(id)}
          position={editingPosition}
          onClose={() => {
            setShowPositionEditor(false);
            setEditingPosition(null);
          }}
          onSave={handlePositionSaved}
        />
      )}
    </div>
  );
}

export default WorkAreaDetail;
